/**
 * AccountRenameModal Component
 *
 * Presentational modal for renaming an account.
 * Pre-fills with current name, validates non-empty.
 * Delegates the actual mutation to the parent via onSubmit prop.
 */

import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
} from '@enfomentis/ui';

export interface AccountRenameModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Current account name to pre-fill */
  currentName: string;
  /** Callback to submit the new name — returns true on success, false on failure */
  onSubmit: (newName: string) => Promise<boolean>;
}

export const AccountRenameModal: React.FC<AccountRenameModalProps> = ({
  isOpen,
  onClose,
  currentName,
  onSubmit,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens or current name changes
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen, currentName]);

  const validateForm = (): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
    if (submitError) {
      setSubmitError(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();

    // Skip if name unchanged
    if (trimmedName === currentName) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);

      const success = await onSubmit(trimmedName);
      if (success) {
        onClose();
      } else {
        setSubmitError('Failed to rename account');
      }
    } catch (error) {
      console.error('Failed to rename account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename account';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="top-center"
    >
      <ModalContent>
        <ModalHeader>Rename Account</ModalHeader>

        <ModalBody className="gap-4">
          <Input
            label="Name"
            placeholder="Enter account name"
            value={name}
            onValueChange={handleNameChange}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccountRenameModal;
